import { Suspense } from 'react';
import { createBrowserRouter, Navigate, useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../layouts/AuthLayout';
import { WorkspaceLayout } from '../layouts/WorkspaceLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { NotFoundPage } from '../pages';
import { logger } from '../utils';
import { lazyWithReload } from './lazyWithReload';

// Lazy-loaded feature pages
const LoginForm = lazyWithReload(() => import('@features/auth/components/LoginForm').then(m => ({ default: m.LoginForm })));
const MagicLinkVerify = lazyWithReload(() => import('@features/auth/components/MagicLinkVerify').then(m => ({ default: m.MagicLinkVerify })));
const InvitationAccept = lazyWithReload(() => import('@features/auth/components/InvitationAccept').then(m => ({ default: m.InvitationAccept })));
const OnboardingPage = lazyWithReload(() => import('@features/ai/pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const UsagePage = lazyWithReload(() => import('@features/usage/pages/UsagePage').then(m => ({ default: m.UsagePage })));
const SettingsPage = lazyWithReload(() => import('@features/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const DocumentViewerPage = lazyWithReload(() => import('@features/dataroom/pages/DocumentViewerPage').then(m => ({ default: m.DocumentViewerPage })));

function LoadingFallback() {
  const { t } = useTranslation();
  return <div className="flex items-center justify-center min-h-screen text-sm text-[var(--color-text-muted)]">{t('core.loading')}</div>;
}

const SuspenseFallback = <LoadingFallback />;

/**
 * Last resort for anything that throws during routing.
 *
 * Without an errorElement React Router renders its raw "Unexpected
 * Application Error!" screen, which tells the user nothing and offers no way
 * out.
 */
function RouteError() {
  const { t } = useTranslation();
  const error = useRouteError();
  logger.error('Unhandled routing error', error);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-lg font-semibold text-[var(--color-text)]">{t('core.routeError.title')}</h1>
      <p className="max-w-md text-sm text-[var(--color-text-secondary)]">
        {t('core.routeError.body')}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="cursor-pointer rounded-lg bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white"
      >
        {t('core.routeError.reload')}
      </button>
    </div>
  );
}

/**
 * Application router configuration
 */
export const router = createBrowserRouter([
  // Auth routes (public)
  {
    element: <AuthLayout />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/login',
        element: <Suspense fallback={SuspenseFallback}><LoginForm /></Suspense>,
      },
      {
        path: '/magic-link',
        element: <Suspense fallback={SuspenseFallback}><MagicLinkVerify /></Suspense>,
      },
      {
        path: '/magic-link/:token',
        element: <Suspense fallback={SuspenseFallback}><MagicLinkVerify /></Suspense>,
      },
      {
        path: '/invitations',
        element: <Suspense fallback={SuspenseFallback}><InvitationAccept /></Suspense>,
      },
      {
        path: '/invitations/:token',
        element: <Suspense fallback={SuspenseFallback}><InvitationAccept /></Suspense>,
      },
    ],
  },

  // Protected routes
  {
    element: <ProtectedRoute />,
    errorElement: <RouteError />,
    children: [
      // Onboarding (fullscreen, no layout)
      {
        path: '/onboarding',
        element: <Suspense fallback={SuspenseFallback}><OnboardingPage /></Suspense>,
      },
      {
        path: '/usage',
        element: <Suspense fallback={SuspenseFallback}><UsagePage /></Suspense>,
      },
      // Settings
      {
        path: '/settings/:section?',
        element: <Suspense fallback={SuspenseFallback}><SettingsPage /></Suspense>,
      },
      // Full-screen document viewer
      {
        path: '/viewer/:projectUuid/:documentUuid',
        element: <Suspense fallback={SuspenseFallback}><DocumentViewerPage /></Suspense>,
      },
      // Unified workspace (chat-first, with sub-routes for panels)
      {
        path: '/chat/:shortId?',
        element: <WorkspaceLayout />,
      },
      // Redirects
      {
        path: '/',
        element: <Navigate to="/chat" replace />,
      },
      {
        path: '/projects',
        element: <Navigate to="/chat" replace />,
      },
      {
        path: '/projects/:uuid/dataroom',
        element: <Navigate to="/chat" replace />,
      },
      {
        path: '/settings/agent',
        element: <Navigate to="/settings" replace />,
      },
    ],
  },

  // 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
